'use client'
import { useLocalStore } from '@/utils/localStore'
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@repo/web-ui/components/base/breadcrumb'
import { Button } from '@repo/web-ui/components/base/button'
import { Separator } from '@repo/web-ui/components/base/separator'
import { SidebarTrigger } from '@repo/web-ui/components/base/sidebar'
import { usePathname } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'
import { Check, Pencil } from 'lucide-react'
import { Input } from '@repo/web-ui/components/base/input'
import { Pitch, Pitcher } from '@/utils/types'

const AppTopbar = () => {
    const pathname = usePathname()
    const crumbs = pathname.split('/').slice(1)
    const { values, setValues } = useLocalStore()
    const [isPitch, setIsPitch] = useState<Pitch>()
    const [isPitcher, setIsPitcher] = useState<Pitcher>()
    const [isEdit, setIsEdit] = useState(false)
    const renameInputRef = useRef<HTMLInputElement>(null)

    const isUUIDv4 = (str: string): boolean => {
      const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidV4Regex.test(str);
    };
    const formatSlug = (slug: string) => {
      // check if slug is uuid (used for pitchers and pitches)
      if (isUUIDv4(slug)) {
        const pitcher = values.PITCHERS.find(pitcher => pitcher.id === slug)
        const pitch = values.PITCHES.find(pitch => pitch.id === slug)
        if (pitcher !== undefined) {
          return pitcher.name
        } 
        if (pitch !== undefined) {
          return pitch.name
        }
      }
      return slug.split('-').join(' ')
    }
    const constructPathname = (crumbIndex: number) => {
        const pathname = '/' + crumbs.slice(0, crumbIndex + 1).join('/')
        return pathname
    }

    // check if path is pitch or pitcher
    useEffect(() => {
      const lastCrumb = crumbs[crumbs.length - 1]
      if (lastCrumb !== undefined) {
        if (isUUIDv4(lastCrumb)) {
          const pitcher = values.PITCHERS.find(pitcher => pitcher.id === lastCrumb)
          const pitch = values.PITCHES.find(pitch => pitch.id === lastCrumb)
          if (pitcher !== undefined) {
            setIsPitch(undefined)
            setIsPitcher(pitcher)
          } else if (pitch !== undefined) {
            setIsPitch(pitch)
            setIsPitcher(undefined)
          }
        } else {
          setIsPitch(undefined)
          setIsPitcher(undefined)
        }
      }
    },[crumbs])

    useEffect(() => {
      if (isEdit && renameInputRef.current) {
        renameInputRef.current.focus()
      }
    },[isEdit, renameInputRef])

  return (
    <header className="sticky top-0 flex h-12 shrink-0 items-center gap-2 z-50 bg-background border-b shadow transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
                <div className="w-full flex items-center gap-2 px-4">
                  <SidebarTrigger className="-ml-1" />
                  <Separator orientation="vertical" className="mr-2 h-4" />
                  <Breadcrumb className='w-full'>
                    <BreadcrumbList>
                    {crumbs.map((crumb, index) => (
                        <React.Fragment key={index}>
                        {/* crumb is not the last */}
                        {index < crumbs.length - 1 && (
                            <>
                            <BreadcrumbItem className="hidden md:block capitalize">
                                <BreadcrumbLink href={constructPathname(index)}>
                                {formatSlug(crumb)}
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator className="hidden md:block" />
                            </>
                        )}
                        {/* crumb is the last */}
                        {index === crumbs.length - 1 && (
                            <BreadcrumbItem className='capitalize w-full max-w-md'>
                              {!isEdit && (
                                <BreadcrumbPage className='truncate'>{formatSlug(crumb)}</BreadcrumbPage>
                              )}
                              {isEdit && (
                                  <Input ref={renameInputRef} data-slot="breadcrumb-page" className='w-full max-w-sm' defaultValue={isPitcher !== undefined ? isPitcher.name : isPitch !== undefined ? isPitch.name : ''} />
                                  )}
                              {(isPitch || isPitcher) && (
                                  <Button size={'icon'} variant={'ghost'} onClick={() => {
                                    if (!isEdit) {
                                      setIsEdit(true)
                                    } else if (isPitcher !== undefined && renameInputRef.current) {
                                      setIsEdit(false)
                                      const updatedPitcher:Pitcher = {...isPitcher, name: renameInputRef.current.value}
                                      setValues ? setValues(prev => ({...prev, PITCHERS: prev.PITCHERS.map(item => item.id === isPitcher.id ? updatedPitcher : item)})) : null
                                    } else if (isPitch !== undefined && renameInputRef.current) {
                                      setIsEdit(false)
                                      const updatedPitch: Pitch = {...isPitch, name: renameInputRef.current.value}
                                      setValues ? setValues(prev => ({...prev, PITCHES: prev.PITCHES.map(item => item.id === isPitch.id ? updatedPitch : item)})) : null
                                    }
                                  }}>
                                    {!isEdit && (
                                      <Pencil />
                                    )}
                                    {isEdit && (
                                      <Check />
                                    )}
                                  </Button>
                              )}
                            </BreadcrumbItem>
                        )}
                        </React.Fragment>
                    ))}
                    </BreadcrumbList>
                  </Breadcrumb>
                  
                </div>
              </header>
  )
}

export default AppTopbar